import Vue from "vue";
import backend from "@/services/backend";

function clearAuthState(context) {
  localStorage.removeItem('sppxToken');
  localStorage.removeItem('refreshToken');
  context.commit("set_sppx_token", null);
  context.commit("set_refresh_token", null);
  if (window.dataLayer) {
    window.dataLayer.push({
      event: "signout",
      user_id: null,
    });
  }
  context.commit("unset_user");
  context.commit("unset_portfolio");
}

export default {
  namespaced: true,
  state: {
    user: null,
    accountHold: null,
    listings: [],
    portfolio: [],
    sppxToken: null,     
    refreshToken: null,   
  },
  mutations: {
    set_user: function (state, val) {
      state.user = val;
      state.accountHold = val && val.account_hold ? val.account_hold : null;
      if (state.accountHold && state.accountHold.active) {
        state.portfolio = [];
      }
    },
    set_user_data: function (state, data) {
      for (const key of Object.keys(data)) {
        Vue.set(state.user, key, data[key]);
      }
      if (Object.prototype.hasOwnProperty.call(data, "account_hold")) {
        state.accountHold = data.account_hold;
        if (state.accountHold && state.accountHold.active) {
          state.portfolio = [];
        }
      }
    },
    set_investor_data: function (state, data) {
      for (const key of Object.keys(data)) {
        Vue.set(state.user.investor_profile, key, data[key]);
      }
    },
    unset_user: function (state) {
      state.user = { authenticated: false, roles: [], homes: [] };
      state.accountHold = null;
    },
    unset_portfolio: function (state) {
      state.portfolio = [];
    },
    push_home(state, val) {
      state.user.homes.push(val);
    },
    push_home_like(state, home_id) {
      state.user.home_likes.push(home_id);
    },
    remove_home_like(state, home_id) {
      state.user.home_likes = state.user.home_likes.filter(
        (x) => x !== home_id
      );
    },
    set_confirmed(state, confirmed_at) {
      state.user.confirmed_at = confirmed_at;
    },
    set_listings(state, listings) {
      state.listings = listings;
    },
   
    set_portfolio(state, portfolio) {
      state.portfolio = portfolio;
    },
    set_sppx_token: (state, sppxToken) => {
      state.sppxToken = sppxToken;
    },
    set_refresh_token: (state, refreshToken) => {
      state.refreshToken = refreshToken;
    },
  },
  getters: {
    user: (state) => state.user,
    account_hold: (state) => state.accountHold,
    is_account_on_hold: (state) => {
      return Boolean(state.accountHold && state.accountHold.active);
    },
    listings: (state) => state.listings,
    portfolio: (state) => state.portfolio,
    is_authenticated: function (state) {
      if (state.user === null) return null;
      else return state.user.authenticated;
    },
    has_role: (state) => (val) => {
      return state.user.roles.indexOf(val) !== -1;
    },
    is_guest: (state) => {
      return state.user.roles.indexOf("guest") !== -1;
    },
    is_investor: (state) => {
      return (
        state.user &&
        state.user.authenticated &&
        state.user.roles.indexOf("investor") !== -1
      );
    },
    is_homeowner: (state) => {
      return state.user.roles.indexOf("homeowner") !== -1;
    },
    sppxToken: (state) => state.sppxToken, 
    refreshToken: (state) => state.refreshToken
  },
  actions: {
    signin: function (context, user) {
      return backend
        .signin(user)
        .then((response) => response.data)
        .then((result) => {
          context.commit("set_user", result);
    
          // Directly call the SPPX signin API without sending sppxUserData
          backend.signinsppx()
            .then((response) => {
              const sppxToken = response.data.token.access_token;
              const refreshToken = response.data.token.refresh_token;
    
              // Store the tokens
              localStorage.setItem('sppxToken', sppxToken);
              localStorage.setItem('refreshToken', refreshToken);
              context.commit("set_sppx_token", sppxToken);
              context.commit("set_refresh_token", refreshToken);
    
              // Trigger analytics event for SPPX login
              window.dataLayer.push({
                event: "login",
                user_id: result.id,
                auth_provider: "sppx",
              });
            })
            .catch((err) => {
              // SPPX login failed, but we continue with normal login
              console.error("SPPX Login failed", err);
            });
    
          // Push analytics event for normal login
          window.dataLayer.push({
            event: "signin",
            user_id: result.id,
            auth_provider: user.provider,
          });
        })
        .catch((err) => {
          context.commit('unset_user');
          throw err;
        });
    },       
    signup: function (context, data) {
      return backend.signup(data).then((response) => {
        context.commit("set_user", response.data);
        backend.signinsppx()
          .then((loginResponse) => {
            const newUserToken = loginResponse.data.token.access_token;
            const newUserrefreshToken = loginResponse.data.token.refresh_token;

            // Commit and store the SPPX token and refresh token
            context.commit("set_sppx_token", newUserToken);
            context.commit("set_refresh_token", newUserrefreshToken);
            localStorage.setItem('sppxToken', newUserToken);
            localStorage.setItem('refreshToken', newUserrefreshToken);

            // Push analytics event for SPPX login
            if (window.dataLayer) {
              window.dataLayer.push({
                event: "login",
                user_id: response.data.id,
                auth_provider: "sppx",
              });
            }
          })
          .catch((err) => {
            // SPPX token sync is needed for payment flows, but it must
            // not make account creation look like a failed signup.
            console.error("SPPX Signup login failed", err);
          });
        return response;
      });
    },
    update_user: function (context, data) {
      return backend.update_profile(data).then((response) => {
        context.commit("set_user_data", response.data);
      });
    },
    clear_session: function (context) {
      clearAuthState(context);
    },
    clear_sppx_session: function (context) {
      // SPPX provider-token teardown ONLY — must NOT touch the Invown login
      // session. clearAuthState/clear_session call unset_user, which wipes
      // store.user and logs the investor out of the app; an SPPX payment-token
      // failure is not an app-auth failure, so we reset just the SPPX tokens.
      localStorage.removeItem("sppxToken");
      localStorage.removeItem("refreshToken");
      context.commit("set_sppx_token", null);
      context.commit("set_refresh_token", null);
    },
    signout: function (context) {
      return backend.signout().finally(function () {
        clearAuthState(context);
      });
    },
    get_user: function (context) {
      return backend
        .me()
        .then((response) => response.data)
        .then((result) => {
          context.commit("set_user", result);
          if (result.authenticated) {
            window.dataLayer.push({
              event: "authenticated",
              user_id: result.id,
            });
          } else {
            window.dataLayer.push({
              event: "unauthenticated",
              user_id: null,
            });
          }
        })
        .catch((error) => {
          // Never block app boot if /api/me fails (network, CORS, auth, etc.).
          context.commit("unset_user");
          // Best-effort analytics; keep it safe.
          try {
            window.dataLayer.push({
              event: "unauthenticated",
              user_id: null,
            });
          } catch (e) {
            // Ignore analytics failures
          }
          return;
        });
    },
    get_listings: function (context, params) {
      return backend
        .get_listings(params)
        .then((response) => response.data)
        .then((result) => {
          context.commit("set_listings", result);
        });
    },
    // payload.view "dashboard": the dashboard's slim portfolio (only the
    // listing fields its cards read).
    get_portfolio: function (context, payload) {
      return backend
        .get_portfolio(payload && payload.view)
        .then((response) => response.data)
        .then((result) => {
          context.commit("set_portfolio", result);
        })
        .catch((error) => {
          // In local/dev, unauthenticated users will get 401. Don't crash the UI.
          if (error?.response?.status === 401) {
            context.commit("unset_portfolio");
            return;
          }
          throw error;
        });
    },
  },
};
